import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

// Material UI
import { Box, Typography } from "@material-ui/core";
import { blue, green, orange, red, yellow } from "@material-ui/core/colors";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@material-ui/icons/Edit";
import WarningIcon from '@mui/icons-material/Warning';

// Custom hooks
import { useToggle } from "hooks/useToggle";
import { useSearch } from "hooks/useSearch";
import { useRuleValidator } from "hooks/useRuleValidator";
import { PERMISSIONS } from "router/permissions";

// Components
import RegularButton from "components/CustomButtons/Button";
import { ModalV2 } from "components/Modal/ModalV2";
import { FormAddTypeMovement } from "./gasMovementsForm/FormAddTypeMovement";
import { GMConfirmationForm } from "./gasMovementsForm/GMConfirmationForm";

// Redux actions
import {
  submitApproval,
  deleteMovement
} from "redux/actions/gasMovementsRequests/gasMovementsRequestActions";
import { getLiquidations } from "redux/actions/liquidations/liquidationsAction";
import { formatCustomDate } from "helpers/dateTools";
import {
  checkInventoryIsOpen,
  OPERATION_INVENTORY_CLOSED_MESSAGE,
} from "hooks/fetch-hooks/useCheckInventoryStatus";

export const columns = [
    {
        "Header": "Fecha",
        "accessor": "dateMovement",
        "filterable": true,
        "filterMethod": (filter, row) => { return row.dateMovement?.toLowerCase().includes(filter.value.toLowerCase()); }
    },
    {
        "Header": "Unidad",
        "accessor": "unit",
        "filterable": true,
        "filterMethod": (filter, row) => { return row.unit?.toLowerCase().includes(filter.value.toLowerCase()); }
    },

    {
        "Header": "F. Nota",
        "accessor": "note",
        "filterable": true,
        "filterMethod": (filter, row) => { return row.note?.toString().toLowerCase()?.includes(filter?.value?.toString().toLowerCase()); }
    },

    {
        "Header": "T. Movimiento",
        "accessor": "movement",
        "filterable": true,
        "filterMethod": (filter, row) => { return row.movement?.toLowerCase().includes(filter.value.toLowerCase()); }
    },
    {
        "Header": "T. Inventario",
        "accessor": "movementType",
        "filterable": true,
        "filterMethod": (filter, row) => { return row.movementType?.toLowerCase().includes(filter.value.toLowerCase()); }
    },
    {
        "Header": "LT",
        "accessor": "liters",
        filterable: true,
        filterMethod: (filter, row) => {
            const formatted = row.liters != null ? parseFloat(row.liters).toFixed(2) : '';
            return formatted.includes(filter.value);
        },
        Cell: props => props.value != null ? parseFloat(props.value).toFixed(2) : null
    },
    {
        "Header": "KG",
        "accessor": "kilograms",
        filterable: true,
        filterMethod: (filter, row) => {
            const formatted = row.kilograms != null ? parseFloat(row.kilograms).toFixed(2) : '';
            return formatted.includes(filter.value);
        },
        Cell: props => props.value != null ? parseFloat(props.value).toFixed(2) : null
    },
    {
        "Header": "Usuario",
        "accessor": "user",
        "filterable": true,
        "filterMethod": (filter, row) => { return row.user?.toLowerCase().includes(filter.value.toLowerCase()); }
    },
    {
        "Header": "Autoriza",
        "accessor": "employee",
        "filterable": true,
        "filterMethod": (filter, row) => { return row.employee?.toLowerCase().includes(filter.value.toLowerCase()); }
    },
    {
        "Header": "Comentario",
        "accessor": "comment",
        "filterable": true,
        "filterMethod": (filter, row) => { return row.comment?.toLowerCase().includes(filter.value.toLowerCase()); }
    },
    {
        "Header": "Acciones",
        "accessor": "status",
        filterable: true,
        filterMethod: (filter, row) => { return row.status?.toLowerCase().includes(filter.value.toLowerCase()); },
        Cell: ({ original }) => {
            const dispatch = useDispatch()
            const { setSnackbar } = useSearch();
            const { user } = useSelector(({ login }) => login);
            const { isLoadingComp } = useSelector(({ search }) => search);
            const [openMov, setOpenMov] = useToggle()
            const [openModal, setOpenModal] = useToggle()
            const [isConfirmModal, setIsConfirmModal] = useState(false)
            const [openCancel, setOpenCancel] = useToggle()
            const [checkingInv, _ci, setCheckingInv] = useToggle()
            const { startDate, endDate, station } = useSelector(({ liquidations }) => liquidations)
            const { unitsMap } = useSelector(({ unitsReducer }) => unitsReducer)
            const unitData = unitsMap.find(unit => unit.number === original.unit)
            const isMaintenance = unitData?.isMaintenance
            const { 
                MOVEMENTS_VIEW_A_R, 
                MOVEMENTS_VIEW_EDIT,
                MOVEMENTS_VIEW_CONFIRM,
                MOVEMENTS_VIEW_DELETE
            } = PERMISSIONS
            const rule = useRuleValidator(MOVEMENTS_VIEW_A_R)
            const ruleEdit = useRuleValidator(MOVEMENTS_VIEW_EDIT)
            const ruleConfirm = useRuleValidator(MOVEMENTS_VIEW_CONFIRM)
            const ruleDelete = useRuleValidator(MOVEMENTS_VIEW_DELETE)

            const handleOpenModal = (val=false) => {
                setOpenModal()
                setIsConfirmModal(val)
            }
            const handleRefreshLiquidations = () => {
                station && dispatch(getLiquidations(startDate, endDate, station))
            }
            const handleResponse = (comment = "") => {
                dispatch(submitApproval(original.id, isConfirmModal, user, comment, handleRefreshLiquidations, setOpenModal))
            }

            const handleDelete = async () => {
                if (checkingInv) return
                if (!station?.id) {
                    setSnackbar("Seleccione una Planta", "danger", "tc")
                    return
                }
                setCheckingInv(true)
                try {
                    const isOpen = await checkInventoryIsOpen(station.id, formatCustomDate(original.dateMovement, '-'))
                    if (!isOpen) {
                        setSnackbar(OPERATION_INVENTORY_CLOSED_MESSAGE, "danger", "tc")
                        return
                    }
                } catch (err) {
                    setSnackbar(`${err || 'Error al consultar el inventario'}`, "danger", "tc")
                    return
                } finally {
                    setCheckingInv(false)
                }

                dispatch(deleteMovement(original.id, setOpenCancel))
            }

            const handleWarning = () => {
                dispatch(setSnackbar(`UNIDAD ${original?.unit} EN MANTENIMIENTO, CONTACTA A SOPORTE EN CASO DE SER NECESARIO`))
            }

            return (
                original?.status !== "AUTORIZADO" && original?.status !== "RECHAZADO" ?
                    <>
                        <div style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            flexWrap: 'wrap',
                        }}>
                            {
                                ruleEdit &&
                                <RegularButton
                                    color="transparent"
                                    round
                                    justIcon
                                    tooltip="Editar Movimiento"
                                    onClick={setOpenMov}
                                    disabled={isMaintenance}
                                >
                                    <EditIcon style={{ color: blue[500] }} />
                                </RegularButton>
                            }
                            {
                                ruleConfirm && rule &&
                                <>
                                    <RegularButton
                                        color="transparent"
                                        round
                                        justIcon
                                        tooltip="Aprobar"
                                        onClick={() => handleOpenModal(true)}
                                        disabled={isMaintenance}
                                    >
                                        <CheckCircleIcon style={{ color: green[500] }} />
                                    </RegularButton>
                                    <RegularButton
                                        color="transparent"
                                        round
                                        justIcon
                                        tooltip="Rechazar"
                                        onClick={() => handleOpenModal(false)}
                                        disabled={isMaintenance}
                                    >
                                        <CancelIcon style={{ color: orange[500] }} />
                                    </RegularButton>
                                </>
                            }
                            {
                                ruleDelete && 
                                <RegularButton
                                    color="transparent"
                                    round
                                    justIcon
                                    tooltip="Eliminar"
                                    onClick={setOpenCancel}
                                    disabled={isMaintenance}
                                >
                                    <DeleteIcon style={{ color: red[500] }} />
                                </RegularButton>
                            }
                            {
                                isMaintenance &&
                                <RegularButton
                                    round
                                    justIcon
                                    color="transparent"
                                    tooltip="En Mantenimiento"
                                    onClick={handleWarning}
                                >
                                    <DeleteIcon style={{ color: red[500] }} />
                                    <WarningIcon style={{ color: yellow[800] }} />
                                </RegularButton>
                            }
                        </div>
                        <ModalV2
                            open={openMov}
                            onClose={setOpenMov}
                            title={`Editar Movimiento ${original.unit}`}
                            justConfirm
                        >
                            <FormAddTypeMovement mov={original} onClose={setOpenMov} />
                        </ModalV2>
                        <ModalV2
                            open={openModal}
                            onClose={setOpenModal}
                            title={isConfirmModal ? `¿Seguro que desea aprobar ${original.unit}?` : `¿Seguro que desea rechazar ${original.unit}?`}
                            justConfirm
                        >
                            <GMConfirmationForm
                                mov={original}
                                onClose={setOpenModal}
                                action={handleResponse}
                                isApproval={isConfirmModal}
                            />
                        </ModalV2>
                        <ModalV2
                            open={openCancel}
                            onClose={setOpenCancel}
                            title={`¿Seguro que desea cancelar ${original.unit}?`}
                            justConfirm
                        >
                            <Box style={{
                                width: "100%",
                                display: "flex",
                                justifyContent: "flex-end",
                                flexWrap: 'wrap',
                                gap: 10
                            }}>

                                <RegularButton
                                    color="transparent"
                                    onClick={setOpenCancel}
                                    loading={isLoadingComp?.GM_approveLoading}
                                >
                                    cancelar
                                </RegularButton>
                                <RegularButton
                                    color="danger"
                                    onClick={() => handleDelete()}
                                    size="sm"
                                    loading={isLoadingComp?.GM_approveLoading}
                                >
                                    Confirmar
                                </RegularButton>
                            </Box>
                        </ModalV2>

                    </>
                    : <Typography variant="caption" style={{
                        color: original?.status === 'AUTORIZADO' ? green[500] : red[500]
                    }}>{original?.status}</Typography>
            )
        }
    }
]